<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';

import WootReports from './components/WootReports.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const ALL_AGENT_REPORT_PERMISSIONS = [
  'administrator',
  'report_manage',
  'report_agents',
];

const route = useRoute();
const router = useRouter();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const agent = useFunctionGetter(
  'agents/getAgentById',
  computed(() => route.params.id)
);

const canViewAllAgents = computed(() =>
  hasPermissions(
    ALL_AGENT_REPORT_PERMISSIONS,
    getUserPermissions(currentUser.value, currentAccountId.value)
  )
);

const isOwnReport = computed(
  () => Number(route.params.id) === Number(currentUser.value?.id)
);

const ensureAccess = () => {
  if (!currentUser.value?.id) return;

  if (isOwnReport.value) {
    router.replace({
      name: 'profile_reports',
      params: { accountId: route.params.accountId },
    });
    return;
  }

  if (canViewAllAgents.value) return;

  router.replace({
    name: 'profile_reports',
    params: { accountId: route.params.accountId },
  });
};

watch([() => route.params.id, currentUser], ensureAccess, { immediate: true });
onMounted(() => store.dispatch('agents/get'));
</script>

<template>
  <WootReports
    v-if="agent.id"
    :key="agent.id"
    type="agent"
    getter-key="agents/getAgents"
    action-key="agents/get"
    :selected-item="agent"
    :show-entity-filter="canViewAllAgents"
    :show-download="canViewAllAgents"
    :download-button-label="$t('AGENT_REPORTS.DOWNLOAD_AGENT_REPORTS')"
    :report-title="$t('AGENT_REPORTS.HEADER')"
    :has-back-button="canViewAllAgents"
  />
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>
